import { OWManager } from "OW.Remoting/Remoting/OWManager";
import { IMultiEditObject } from "UI/UI.Controls/DataSource/MultiEdit/MEOWObject";
import { ICryptographicInfo } from "../../../../../Global/Base/Crypto/ICryptographicInfo";
import { ISignatureInfo } from "../../../../../Global/Base/Crypto/ISignatureInfo";
import { NullOWObject, OWObject } from "../../../../../OW.Remoting/Remoting/DataTypes/OWObject";
import { ActionContext, ActionType } from "../../../../UI.Controls/Actions/IAction";
import { DropDownMenu } from "../../../../UI.Controls/DropDownMenu/DropDownMenu";
import { ControlParams } from "../../Controls/ControlInterfaces";
import { LocalizedStringBuilder } from "../../DataBuilders/LocalizedStringBuilder";
import { CryptoInfo } from "../../Helpers/CryptoInfo";
import { MessageWindow } from "../../MessageWindow";
import { IFrame } from "../IFrame";
import { XGAction } from "./XGAction";

export class CryptoLabelAction extends XGAction {
    private _currentSignStatus: ISignatureInfo = null;
    private _currentSignTooltip: string = "";
    private _dropDownMenu: DropDownMenu;
    public readonly masterAttr: string = null;

    public override set owner(val: IFrame) {
        super.owner = val;
        this._setActualObjectListeners();
        this._updateStatus(this.owner.actualObject);
    }

    public override get owner(): IFrame {
        return super.owner;
    }

    public override get context(): ActionContext {
        return ActionContext.frameAction;
    }

    public override get actionType(): ActionType {
        return ActionType.cryptoLabel;
    }

    constructor(params: ControlParams) {
        super(params);

        this.element.classList.add("crypto-label");
        //this.LocKey = "GUI-629703"; // Není vytvořen podpis
        this.masterAttr = this.element.getAttribute("data-masterattr");
    }

    public updateSignedState(signatureInfo?: ISignatureInfo): void {
        if (signatureInfo) {
            this._currentSignStatus = signatureInfo;
        }
        if (this.toolbarButton) {
            this.toolbarButton.element.title = this._currentSignTooltip;
        }
        this._updateSignedState({ signatureInfo: this._currentSignStatus });
    }

    /**
     * Poslouchá změnám atributu aktuálně vybraného objektu. Pokud se změnil podpis, tak aktualizuje svůj stav.
     * Pozn.: Vzniklo jako řešení situace, kdy změna se odehraje na detailu ale cryptolabelaction je v masteru.
     */
    private _setActualObjectListeners(): void {
        const listenerKey = "claObjectAttributeChangedListener";
        if (this.owner.actualObject) {
            this._events.addWithKey(listenerKey, this.owner.actualObject.attributeChanged, this._actualObjectAttributeChangedListener.bind(this));
        }

        this._events.add(this.owner.actualObjectChanged, async (_sender, eventArgs) => {
            try {
                this._events.removeByKey(listenerKey);
                if (this.toolbarButton) {
                    this.toolbarButton.showLoader();
                }
                if (eventArgs && eventArgs.actualObject) {
                    this._events.addWithKey(listenerKey, eventArgs.actualObject.attributeChanged, this._actualObjectAttributeChangedListener.bind(this));
                    await this._updateStatus(await this._getObjectForDisplay(eventArgs.actualObject));
                }
            } finally {
                if (this.toolbarButton) {
                    this.toolbarButton.hideLoader();
                }
            }
        });
    }

    /** Vrací objekt, pro zobrazení informací o podpisu. */
    private async _getObjectForDisplay(obj: OWObject): Promise<OWObject> {
        if (!this.masterAttr) {
            return obj;
        }

        const masterAttrObj = await obj.getValue(this.masterAttr) as OWObject;
        if (!masterAttrObj || masterAttrObj instanceof NullOWObject) {
            return null;
        }

        await OWManager.publishInstance(obj.instanceID, this.masterAttr, this._getRequestContext());
        return masterAttrObj;
    }

    private async _actualObjectAttributeChangedListener(sender: OWObject, attr: string): Promise<void> {
        if (sender && attr === "DigitalSignature" || sender && this.masterAttr && this.masterAttr === attr) {
            this._updateStatus(await this._getObjectForDisplay(sender));
        }
    }

    /**
     * Aktualizuje informace a zobrazí je v GUI
     */
    private async _updateStatus(obj: OWObject): Promise<void> {
        try {
            this.showLoader();
            const cryptoInfo = obj?.instanceID && !(obj instanceof IMultiEditObject) ? await CryptoInfo.getCryptographicInfo(obj.instanceID, this._getRequestContext()) : undefined;
            this._updateSignedState(cryptoInfo);
            this._createTooltip(cryptoInfo);
            this._currentSignStatus = cryptoInfo ? cryptoInfo.signatureInfo : undefined;
        } finally {
            this.hideLoader();
        }
    }

    protected override async _internalExecute(): Promise<void> {
        try {
            if (this._dropDownMenu) {
                this._dropDownMenu = null;
                return;
            }
            this.showLoader();
            const obj = await this._getObjectForDisplay(this.owner.actualObject);
            const cInfo = obj ? await CryptoInfo.getCryptographicInfo(obj.instanceID, this._getRequestContext()) : undefined;
            if (cInfo && (
                (cInfo.signatureInfo && CryptoInfo.isSigned(cInfo.signatureInfo)) || //objekt je podepsán
                (cInfo.encryptionInfo && cInfo.encryptionInfo.encrypted))) { //objekt je zašifrován
                CryptoInfo.getCryptoInfoDropDown(cInfo, this.owner.actualObject).then((dropDown) => {
                    this._dropDownMenu = dropDown;
                    const showOnElement = (this.toolbarButton != null) ? this.toolbarButton.element : this.element;
                    dropDown.show(showOnElement, null);
                    this._events.add(this._dropDownMenu.onClose, () => {
                        this._dropDownMenu = null;
                    }, { oneTimeOnly: true });
                });
            } else {
                MessageWindow.showAlert(new LocalizedStringBuilder("GUI-628975")); // Digitální podpis je prázdný
            }
        } finally {
            this.hideLoader();
        }
    }

    private _updateSignedState(cryptoInfo: ICryptographicInfo): void {
        const icon = this.toolbarButton && this.toolbarButton.element.querySelector(".Icon");

        this.element.classList.remove("warning", "encrypted", "unknown");
        icon && icon.classList.remove("warning", "encrypted", "unknown");

        const signatureInfo = cryptoInfo && cryptoInfo.signatureInfo;
        if (signatureInfo) {
            const empty = CryptoInfo.isEmpty(signatureInfo);
            const isSignatureEncrypted = CryptoInfo.isEncrypted(cryptoInfo);
            const signed = CryptoInfo.isSigned(signatureInfo) && !isSignatureEncrypted;

            const warningClass = isSignatureEncrypted ? false : CryptoInfo.getSignatureWarningClass(signatureInfo);

            this.element.classList.toggle("signed", signed);
            icon && icon.classList.toggle("signed", signed);

            if (!empty && warningClass) {
                this.element.classList.add(warningClass);
                icon && icon.classList.add(warningClass);
            }
        } else {
            this.element.classList.remove("signed");
            icon && icon.classList.remove("signed");
        }
        if (cryptoInfo && cryptoInfo.encryptionInfo) {
            this.element.classList.toggle("encrypted", cryptoInfo.encryptionInfo.encrypted);
            icon && icon.classList.toggle("encrypted", cryptoInfo.encryptionInfo.encrypted);
        }
    }

    private async _createTooltip(cryptoInfo: ICryptographicInfo): Promise<void> {
        if (!cryptoInfo) {
            return;
        }
        const tooltip = await CryptoInfo.getCryptoInfoToolTip(cryptoInfo.signatureInfo, cryptoInfo.encryptionInfo);
        if (tooltip.length > 0) {
            this.element.setAttribute("title", tooltip);
            this._currentSignTooltip = tooltip;
            if (this.toolbarButton) {
                this.toolbarButton.element.title = tooltip;
            }
        }
    }
}
